import React, { useContext } from 'react';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { Order } from '../../../types/order';
import { ProductTransactionContext } from '../../../context/ProductTransactionContext';
import Provider from '../../../providers/common/Provider';
import { PdfTemplate, PdfTemplateEntitySubtype, PdfTemplateInput } from '../../../types/pdf_template';
import PdfTemplatePropertiesEditor from './PdfTemplatePropertiesEditor';
import {
  PdfBorderStyle,
  PdfComponentType,
  PdfImageFit,
  PdfOrientation,
  PdfPageFormat,
  PdfRepeat,
  PdfTableType,
  PdfTextAlign,
  PdfVerticalAlign,
} from './PdfComponent';
import { getOrderDataKeys, getProductTransactionDataKeys, PdfSubkey } from './data/PdfDataKey';
import { v4 } from 'uuid';
import {
  BarcodeExportConfigurationField,
  BarcodeExportConfigurationFormat,
} from '../../../types/barcode_export_configuration';
import { getDefaultTableColumnsForTableType } from './PdfTemplateEditorContext';

interface PdfTemplatePropertiesEditorProps {
  template: PdfTemplate;
  onDone: (template: PdfTemplateInput) => void;
}

export default function PdfTemplateEditor({ template, onDone }: PdfTemplatePropertiesEditorProps) {
  return (
    <Provider order container bin lot productTransaction customField stockLocation productMasterData>
      <Element template={template} onDone={onDone} />
    </Provider>
  );
}

function Element({ template, onDone }: PdfTemplatePropertiesEditorProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { productTransactions } = useContext(ProductTransactionContext);

  const validSubkeys = new Set<PdfSubkey>();

  if (template.entitySubtype === PdfTemplateEntitySubtype.inbound) validSubkeys.add(PdfSubkey.destination);
  else if (template.entitySubtype === PdfTemplateEntitySubtype.outbound) validSubkeys.add(PdfSubkey.source);
  else if (template.entitySubtype === PdfTemplateEntitySubtype.move) {
    validSubkeys.add(PdfSubkey.destination);
    validSubkeys.add(PdfSubkey.source);
  }

  const dataKeys = getOrderDataKeys(customFields).filter(dk => !dk.subkey || validSubkeys.has(dk.subkey));
  const imageDataKeys = getOrderDataKeys(customFields, true).filter(dk => !dk.subkey || validSubkeys.has(dk.subkey));

  return (
    <PdfTemplatePropertiesEditor<Order>
      properties={template.properties}
      onDone={properties => {
        template.properties = properties;
        onDone(new PdfTemplateInput(template));
      }}
      settings={{
        pageFormats: [PdfPageFormat.A4, PdfPageFormat.A3],
        orientations: [PdfOrientation.portrait, PdfOrientation.landscape],
        repeatModes: [PdfRepeat.always, PdfRepeat.first, PdfRepeat.last, PdfRepeat.attachment],
        defaultZoom: 1,
        barcodeExportEntries: [],
        defaultGridSizeMm: 5,
      }}
      defaultComponents={{
        staticText: () => ({
          id: v4(),
          border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
          type: PdfComponentType.staticText,
          x: 20,
          y: 20,
          width: 40,
          height: 10,
          text: 'Static text',
          repeatMode: PdfRepeat.always,
          align: PdfTextAlign.left,
          valign: PdfVerticalAlign.top,
        }),
        barcode: () => ({
          id: v4(),
          type: PdfComponentType.barcode,
          x: 20,
          y: 100,
          width: 50,
          height: 20,
          key: dataKeys[0],
          repeatMode: PdfRepeat.always,
          entry: {
            format: BarcodeExportConfigurationFormat.code128,
            height: 20,
            horizontalMargin: 2,
            verticalMargin: 2,
            includeText: true,
            field: BarcodeExportConfigurationField.productNumber,
            id: v4(),
          },
        }),
        image: () => ({
          id: v4(),
          type: PdfComponentType.image,
          x: 20,
          y: 60,
          width: 50,
          height: 30,
          src: undefined,
          fit: PdfImageFit.contain,
          opacity: 1,
          repeat: true,
          repeatMode: PdfRepeat.always,
        }),
        field: () => ({
          id: v4(),
          border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
          type: PdfComponentType.field,
          x: 20,
          y: 20,
          width: 40,
          height: 10,
          key: dataKeys[0],
          repeatMode: PdfRepeat.always,
          align: PdfTextAlign.left,
          valign: PdfVerticalAlign.top,
        }),
        table: tableType => ({
          repeatMode: PdfRepeat.always,
          tableType: tableType,
          id: v4(),
          type: PdfComponentType.table,
          x: 10,
          y: 40,
          width: 190,
          height: 140,
          sorting: [],
          rowHeight: 8,
          header: true,
          borderCellsHorizontal: true,
          borderCellsVertical: true,
          border: { enabled: true, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
          columns: getDefaultTableColumnsForTableType(tableType),
        }),
      }}
      dataKeys={dataKeys}
      imageDataKeys={imageDataKeys}
      tableProviders={[
        {
          tableType: PdfTableType.productTransaction,
          columns: getProductTransactionDataKeys(customFields, [...validSubkeys]).filter(
            dk => !dk.subkey || validSubkeys.has(dk.subkey),
          ),
          rowResolver: (order?: Order) => {
            if (!order) return [];
            return [...productTransactions.values()].filter(pt => pt.parentId === order.id);
          },
        },
      ]}
    />
  );
}
